import { UserGroup } from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { useGroupRules, UserGroupRules, useUserGroups } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import {
  UpdateUserFormModel,
  UpdateUserPermissionsFormFields,
  UserGroupOption,
} from './types';

export const UpdateUserPermissionsModal = () => {
  const { closeModal } = useModalMethods(Modals.UpdateUserPermissions);
  const { isOpened, modalProps } = useModalState(Modals.UpdateUserPermissions);
  const { userGroups } = useUserGroups();
  const { groupRules } = useGroupRules();

  const userGroupOptions = useMemo(() => {
    const availableUserGroups = filterUserGroupsBasedOnRule(
      userGroups as UserGroup[],
      groupRules as Record<UserGroupRules, boolean>,
    );

    return availableUserGroups.map(userGroupToUserGroupOption);
  }, [groupRules, userGroups]);

  const selectedOptions = useMemo(() => {
    const userGroups = modalProps?.user?.userGroups ?? [];
    const selectedGroups = filterUserGroupsBasedOnRule(
      userGroups,
      groupRules as Record<UserGroupRules, boolean>,
    );

    const selectedGroupsSet = new Set<string>(
      selectedGroups.map((item: { id: string }) => item.id),
    );

    return userGroupOptions.filter((item) => selectedGroupsSet.has(item.id));
  }, [userGroupOptions, modalProps?.user, groupRules]);

  const methods = useForm<UpdateUserFormModel>({
    mode: 'all',
    values: {
      [UpdateUserPermissionsFormFields.UserGroups]: selectedOptions,
    },
  });

  const { formState, reset, handleSubmit } = methods;

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onFormSubmit = useCallback(
    (formValues: UpdateUserFormModel) => {
      modalProps?.onSubmit({
        userId: modalProps.user.id!,
        userGroups: formValues.userGroups.map((item) => ({
          id: item.id,
          name: item.label,
          isPublic: item.isPublic,
        })),
      });
      closeModalAndResetForm();
    },
    [modalProps, closeModalAndResetForm],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Update User permissions"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'update-user-permissions-cancel' },
        {
          label: 'Save permissions',
          kind: 'primary',
          onClick: handleSubmit(onFormSubmit),
          disabled: !formState.isValid,
          testId: 'update-user-permissions-submit',
        },
      ]}
      onClose={closeModalAndResetForm}
      data-testid="update-user-permissions-modal"
    >
      <GenericForm<UpdateUserFormModel> formMethods={methods}>
        <FormAutocomplete
          type="autocomplete"
          data-testid="update-user-permissions-groups"
          multiple
          name={UpdateUserPermissionsFormFields.UserGroups}
          label="User permissions"
          options={userGroupOptions}
        />
      </GenericForm>
    </ModalContainer>
  );
};

function userGroupToUserGroupOption(item: UserGroup): UserGroupOption {
  return {
    id: item.id,
    value: item.id,
    label: item.name,
    isPublic: item.isPublic,
  };
}

function filterUserGroupsBasedOnRule(
  userGroups: UserGroup[],
  groupRules: Record<UserGroupRules, boolean>,
): UserGroup[] {
  const groups = userGroups || [];
  return groupRules[UserGroupRules.AccessToUpdatePrivateGroups]
    ? groups
    : groups.filter((item) => item.isPublic);
}
